import { useSyncExternalStore } from "react";

/**
 * Is the page helper switched on for this store?
 *
 * Local and per store, like the rest of the onboarding aids
 * (`onboarding/setup-progress.ts`): this is a reading preference for whoever is
 * sitting at this browser, not a property of the business. Default OFF — the
 * helper is something the seller asks for from Settings, not a floating button
 * every established store has to dismiss.
 */
const key = (storeSlug: string) => `a2z:page-help:v1:${storeSlug}`;

export function isPageHelpEnabled(storeSlug: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(key(storeSlug)) === "1";
  } catch {
    return false;
  }
}

export function setPageHelpEnabled(storeSlug: string, enabled: boolean): void {
  try {
    localStorage.setItem(key(storeSlug), enabled ? "1" : "0");
    // Same-tab listeners: `storage` only fires in OTHER tabs, so the launcher
    // would not appear until a reload without this.
    window.dispatchEvent(new CustomEvent(PAGE_HELP_CHANGED));
  } catch {
    /* private browsing — the toggle just does not stick */
  }
}

export const PAGE_HELP_CHANGED = "a2z:page-help-changed";

/**
 * The same answer, as REACTIVE state.
 *
 * `isPageHelpEnabled` reads localStorage, which React has no way to notice
 * changing. Anything that renders differently once the helper is on — the
 * setup checklist, whose last step IS this switch — has to subscribe, or it
 * keeps showing the step the seller has just completed and the floating corner
 * never hands over.
 */
export function usePageHelpEnabled(storeSlug: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      // `storage` covers other tabs; the custom event covers this one.
      window.addEventListener(PAGE_HELP_CHANGED, onChange);
      window.addEventListener("storage", onChange);
      return () => {
        window.removeEventListener(PAGE_HELP_CHANGED, onChange);
        window.removeEventListener("storage", onChange);
      };
    },
    () => isPageHelpEnabled(storeSlug),
    () => false,
  );
}
